import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack } from '@mui/material';
import { TextField } from 'formik-mui';
import { Field, Form, Formik } from 'formik';
import { useEffect } from 'react';
import { CreateEventDto } from '@sr/dto';
import { useSnack } from 'shared/ui/snack';
import { formatPerson } from 'utils/person-format-helpers';
import { isDateBeforeToday } from 'utils/date-validation';
import { DatePickerField } from 'shared/ui/formik-fields/date-picker';
import { useStateSelector } from 'shared/store';
import { useAddEvent } from 'features/events/events-actions/event-actions.hook';
import { useCompanyEmployee } from 'entities/company/company-persons.hook';
import { EventTaskType } from '@sr/dto/dist/src/enums/events';

export type FormData = {
  contactPersonId: number | undefined,
  content: string,
  type: EventTaskType | '',
  dueDate: Date | undefined;
};

type ValidationData = Partial<{ [x in keyof FormData]: string }>;


const initialValues: FormData = {
  contactPersonId: undefined,
  content: '',
  type: '',
  dueDate: undefined
};

type DialogProps = {
  open: boolean;
  companyId: number;
  onClose: () => void;
};

const AddEventDialog = (props: DialogProps) => {
  const {
    open,
    onClose,
    companyId } = props;

  const user = useStateSelector(store => store.auth.user);

  const { collection: companyEmployees, error } = useCompanyEmployee(companyId);
  const addEvent = useAddEvent(companyId);
  const { showError, showSuccess } = useSnack();

  useEffect(() => {
    if (open) {
      if (error)
        showError(`Ошибка получения контактных лиц компании: ${error}`);
    }
  }, [open, error, showError]);

  const validate = (values: FormData): ValidationData => {
    let validationResult: ValidationData = {};

    if (!values.content)
      validationResult.content = 'Обязательное поле';

    if (values.dueDate && isDateBeforeToday(values.dueDate))
      validationResult.dueDate = 'Дата контакта не может быть раньше текущей';

    return validationResult;
  };

  const handleSubmit = async (values: FormData) => {
    if (user === null) {
      throw new Error('Please login before adding events');
    }

    const newEvent: CreateEventDto = {
      taskType: EventTaskType.CASE,
      content: values.content,
      contactPersonId: values.contactPersonId,
      dueDate: values.dueDate
    };
    await addEvent(newEvent)
      .then(() => showSuccess('Событие успешно добавлено'))
      .catch((e) => { showError(`Ошибка добавления события: ${e.message}`) })
      .finally(() => { onClose() });
  };

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle>Новое событие</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={validate}
        >
          {({ submitForm, isSubmitting }: any) => (
            <Form>
              <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
                <Field
                  name='dueDate'
                  component={DatePickerField}
                  label="Дата следующего контакта"
                />
                <Field
                  name='contactPersonId'
                  label='Кому'
                  component={TextField}
                  select
                >
                  {companyEmployees.map(p => (
                    <MenuItem
                      key={p.id}
                      value={p.id}
                    >
                      {formatPerson(p.person)}
                    </MenuItem>
                  ))}
                </Field>
                <Field
                  name='content'
                  label='Текст'
                  multiline
                  rows={4}
                  component={TextField}
                />
              </Stack>
              <DialogActions>
                <Button variant="contained"
                  autoFocus
                  onClick={submitForm}
                  disabled={isSubmitting}>
                  {isSubmitting ? 'Подождите...' : 'Сохранить'}
                </Button>
                <Button variant="outlined"
                  onClick={onClose}
                  disabled={isSubmitting}>
                  Закрыть
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog >);
};

export default AddEventDialog;


